import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, MapPin, Users, Euro, Plus, Edit2, Trash2 } from 'lucide-react';
import { mockSessions } from '../data/mockData';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Calendrier = () => {
  const { toast } = useToast();
  const [sessions, setSessions] = useState(mockSessions);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newSession, setNewSession] = useState({
    formationType: 'interne',
    category: '',
    formationName: '',
    startDate: '',
    endDate: '',
    location: '',
    availableSeats: '',
    totalSeats: '',
    price: '',
    status: 'open'
  });

  const handleAddSession = (e) => {
    e.preventDefault();
    const session = {
      id: String(Date.now()),
      ...newSession,
      availableSeats: parseInt(newSession.availableSeats),
      totalSeats: parseInt(newSession.totalSeats)
    };
    
    setSessions([...sessions, session]);
    setIsAddDialogOpen(false);
    setNewSession({
      formationType: 'interne',
      category: '',
      formationName: '',
      startDate: '',
      endDate: '',
      location: '',
      availableSeats: '',
      totalSeats: '',
      price: '',
      status: 'open'
    });
    
    toast({
      title: "Session ajoutée !",
      description: "La nouvelle session a été ajoutée au calendrier.",
    });
  };

  const handleDeleteSession = (id) => {
    setSessions(sessions.filter(s => s.id !== id));
    toast({
      title: "Session supprimée",
      description: "La session a été retirée du calendrier.",
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=1920&h=400&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80"></div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="mb-4 text-sm">
            <Link to="/" className="text-gray-300 hover:text-white">Accueil</Link>
            <span className="mx-2 text-red-500">/</span>
            <span className="text-red-500">Calendrier</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide">
            Calendrier des sessions
          </h1>
          
          <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto">
            Consultez et réservez vos prochaines formations
          </p>
        </div>
      </div>

      {/* Add Session Button */}
      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Prochaines sessions</h2>
              <p className="text-gray-600">Vous avez {sessions.length} session{sessions.length > 1 ? 's' : ''} programmée{sessions.length > 1 ? 's' : ''}</p>
            </div>
            
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700 text-white">
                  <Plus size={20} className="mr-2" />
                  Ajouter une session
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Ajouter une nouvelle session</DialogTitle>
                  <DialogDescription>
                    Remplissez les informations de la session de formation
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleAddSession} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Catégorie</Label>
                      <Input
                        id="category"
                        value={newSession.category}
                        onChange={(e) => setNewSession({...newSession, category: e.target.value})}
                        required
                        placeholder="Ex: SSIAP, Secourisme..."
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="formationName">Nom de la formation</Label>
                      <Input
                        id="formationName"
                        value={newSession.formationName}
                        onChange={(e) => setNewSession({...newSession, formationName: e.target.value})}
                        required
                        placeholder="Ex: SSIAP 1 - Initiale"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="startDate">Date de début</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={newSession.startDate}
                        onChange={(e) => setNewSession({...newSession, startDate: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="endDate">Date de fin</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={newSession.endDate}
                        onChange={(e) => setNewSession({...newSession, endDate: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="location">Lieu</Label>
                    <Input
                      id="location"
                      value={newSession.location}
                      onChange={(e) => setNewSession({...newSession, location: e.target.value})}
                      required
                      placeholder="Ex: Paris, Lyon..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="totalSeats">Places totales</Label>
                      <Input
                        id="totalSeats"
                        type="number"
                        value={newSession.totalSeats}
                        onChange={(e) => setNewSession({...newSession, totalSeats: e.target.value, availableSeats: e.target.value})}
                        required
                        min="1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="price">Tarif</Label>
                      <Input
                        id="price"
                        value={newSession.price}
                        onChange={(e) => setNewSession({...newSession, price: e.target.value})}
                        required
                        placeholder="Ex: 850€"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="status">Statut</Label>
                      <select
                        id="status"
                        value={newSession.status}
                        onChange={(e) => setNewSession({...newSession, status: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="open">Ouvert</option>
                        <option value="full">Complet</option>
                        <option value="cancelled">Annulé</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" className="bg-red-600 hover:bg-red-700">
                      Ajouter la session
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Sessions List */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-6">
            {sessions.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <CalendarIcon size={64} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Aucune session programmée</h3>
                <p className="text-gray-600">Ajoutez votre première session de formation</p>
              </div>
            ) : (
              sessions.map((session) => (
                <div 
                  key={session.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all p-6 border-l-4 ${
                    session.status === 'full' ? 'border-gray-400' : 
                    session.status === 'cancelled' ? 'border-red-400' :
                    'border-red-600'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-red-100 text-red-700 text-xs px-3 py-1 rounded-full font-semibold">
                              {session.category}
                            </span>
                            {session.status === 'full' && (
                              <span className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full font-semibold">
                                COMPLET
                              </span>
                            )}
                            {session.status === 'cancelled' && (
                              <span className="bg-red-200 text-red-700 text-xs px-3 py-1 rounded-full font-semibold">
                                ANNULÉ
                              </span>
                            )}
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {session.formationName}
                          </h3>
                        </div>
                        
                        <div className="flex gap-2">
                          <button className="text-gray-500 hover:text-red-600 transition-colors">
                            <Edit2 size={20} />
                          </button>
                          <button 
                            onClick={() => handleDeleteSession(session.id)}
                            className="text-gray-500 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-gray-600">
                        <div className="flex items-center gap-2">
                          <CalendarIcon size={18} className="text-red-600" />
                          <span className="text-sm">
                            {formatDate(session.startDate)} - {formatDate(session.endDate)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <MapPin size={18} className="text-red-600" />
                          <span className="text-sm">{session.location}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Users size={18} className="text-red-600" />
                          <span className="text-sm">
                            {session.availableSeats} / {session.totalSeats} places disponibles
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-4">
                      {session.status === 'open' && session.availableSeats > 0 && (
                        <Button className="bg-red-600 hover:bg-red-700 text-white whitespace-nowrap">
                          RÉSERVER MAINTENANT
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calendrier;